/**
 * React ErrorBoundary 組件 | React ErrorBoundary Component
 * 捕獲子元件樹中的 JavaScript 錯誤，顯示備用 UI
 * Catches JavaScript errors in child component tree and displays fallback UI
 */

import { Component, ReactNode } from 'react';
import { ErrorBoundaryFallback } from './EmptyStates';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary 類別組件
 * 使用類別組件是因為需要使用 componentDidCatch 生命週期
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // 更新 state 使下一次渲染能夠顯示降級後的 UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 可以將錯誤日誌上報給伺服器
    console.error('ErrorBoundary 捕獲到錯誤:', error);
    console.error('錯誤詳情:', errorInfo);

    // 呼叫自定義錯誤處理回調
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定義 fallback，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 否則使用預設的 ErrorBoundaryFallback
      return (
        <ErrorBoundaryFallback
          error={this.state.error || undefined}
          resetError={this.handleReset}
          componentName="應用程式"
        />
      );
    }

    return this.props.children;
  }
}

/**
 * HOC: withErrorBoundary
 * 為任何組件添加錯誤邊界保護
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WithErrorBoundary = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
}
